import { ExternalLink, GraduationCap, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { locale, tx } from '../i18n';
import { formatDate, formatNumber } from '../lib/format';
import type { NearbySchool, NearbySchools } from '../types/api';
import ReportSection from './report/ReportSection';

interface NearbySchoolsSectionProps {
  schools: NearbySchools;
  /** When the report read its sources, for the line at the foot of the section. */
  checkedAt: string;
}

type Phase = 'primary' | 'secondary';

function phases(): Array<{ key: Phase; label: string; ages: string }> {
  return [
    { key: 'primary', label: tx('Primary', 'İlkokul',
    'Podstawowa',
    'Primária',
    'Primaria'), ages: tx('ages 4 to 11', '4-11 yaş',
    'wiek 4–11',
    'dos 4 aos 11 anos',
    'de 4 a 11 años') },
    { key: 'secondary', label: tx('Secondary', 'Ortaokul / lise',
    'Ponadpodstawowa',
    'Secundária',
    'Secundaria'), ages: tx('ages 11 to 16 or 18', '11-16 veya 18 yaş',
    'wiek 11–16 lub 18',
    'dos 11 aos 16 ou 18 anos',
    'de 11 a 16 o 18 años') },
  ];
}

/**
 * Schools around the address. Informational only: it is not part of the score, because it matters
 * a great deal to some tenants and not at all to others.
 */
export default function NearbySchoolsSection({ schools, checkedAt }: NearbySchoolsSectionProps) {
  const radiusKm = (schools.radiusMetres / 1000).toLocaleString(locale(), {
    maximumFractionDigits: 1,
  });
  const empty = schools.primary.length === 0 && schools.secondary.length === 0;
  const [phase, setPhase] = useState<Phase>(
    schools.primary.length === 0 && schools.secondary.length > 0 ? 'secondary' : 'primary',
  );
  const list = phase === 'primary' ? schools.primary : schools.secondary;
  const all = phases();
  const current = all.find((entry) => entry.key === phase) ?? all[0];

  return (
    <ReportSection
      id="section-schools"
      icon={GraduationCap}
      title={tx('Schools nearby', 'Yakındaki okullar',
    'Szkoły w pobliżu',
    'Escolas nas imediações',
    'Colegios cercanos')}
      note={tx(
        `Within ${radiusKm} km, nearest first.`,
        `${radiusKm} km içinde, en yakından başlayarak.`,
    `W promieniu ${radiusKm} km, od najbliższych.`,
    `Até ${radiusKm} km, das mais próximas primeiro.`,
    `Hasta ${radiusKm} km, empezando por los más cercanos.`,
      )}
      informational
      quality="verified"
      source={{
        name: tx(
          'Get Information about Schools, with Ofsted judgements',
          'Get Information about Schools ve Ofsted değerlendirmeleri',
          'Get Information about Schools wraz z ocenami Ofsted',
          'Get Information about Schools, com as avaliações do Ofsted',
          'Get Information about Schools, con las valoraciones de Ofsted',
        ),
        version: schools.dataVersion,
        checked: checkedAt,
      }}
    >

      {empty ? (
        <p className="small muted">
          {tx(
            `No mainstream schools are recorded within ${radiusKm} km.`,
            `${radiusKm} km içinde kayıtlı bir okul yok.`,
    `Brak szkół ogólnodostępnych w promieniu ${radiusKm} km.`,
    `Não há escolas regulares registadas até ${radiusKm} km.`,
    `No constan colegios ordinarios a menos de ${radiusKm} km.`,
          )}
        </p>
      ) : (
        <>
          <div className="phase-tabs" role="tablist" aria-label={tx('School phase', 'Okul kademesi',
    'Etap edukacyjny',
    'Ciclo de ensino',
    'Etapa educativa')}>
            {all.map((entry) => {
              const count = entry.key === 'primary' ? schools.primary.length : schools.secondary.length;
              return (
                <button
                  key={entry.key}
                  type="button"
                  role="tab"
                  id={`schools-tab-${entry.key}`}
                  aria-selected={entry.key === phase}
                  aria-controls="schools-panel"
                  className={entry.key === phase ? 'phase-tab phase-tab--active' : 'phase-tab'}
                  onClick={() => setPhase(entry.key)}
                >
                  {entry.label}
                  <span className="phase-tab__count">{count}</span>
                </button>
              );
            })}
          </div>

          <div id="schools-panel" role="tabpanel" aria-labelledby={`schools-tab-${phase}`} className="stack stack--tight">
            <p className="tiny muted">
              {tx(
                `${current?.label} schools, ${current?.ages}. Schools teaching every age appear under both.`,
                `Kademe: ${current?.label}, ${current?.ages}. Her yaşa eğitim veren okullar iki sekmede de görünür.`,
    `Szkoły: ${current?.label}, ${current?.ages}. Szkoły uczące wszystkich roczników pojawiają się w obu zakładkach.`,
    `Escolas ${current?.label}, ${current?.ages}. As escolas que ensinam todas as idades aparecem nas duas.`,
    `Colegios ${current?.label}, ${current?.ages}. Los centros que cubren todas las edades aparecen en ambas.`,
              )}
            </p>
            {list.length === 0 ? (
              <p className="small muted">
                {tx('None within the search radius.', 'Arama yarıçapında yok.',
    'Brak w promieniu wyszukiwania.',
    'Nenhuma dentro do raio de pesquisa.',
    'Ninguno dentro del radio de búsqueda.')}
              </p>
            ) : (
              <ul className="school-list">
                {list.map((school) => (
                  <SchoolItem key={school.urn} school={school} />
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <p className="tiny muted">{schools.note}</p>
      <p className="tiny muted">
        {tx(
          'Sources: Get Information about Schools (Department for Education) and Ofsted management information, Open Government Licence.',
          'Kaynaklar: Get Information about Schools (Eğitim Bakanlığı) ve Ofsted yönetim bilgileri, Open Government Licence.',
    'Źródła: Get Information about Schools (Department for Education) i dane zarządcze Ofsted, Open Government Licence.',
    'Fontes: Get Information about Schools (Department for Education) e informação de gestão do Ofsted, Open Government Licence.',
    'Fuentes: Get Information about Schools (Department for Education) e información de gestión de Ofsted, Open Government Licence.',
        )}
      </p>
    </ReportSection>
  );
}

/** GIAS establishment kinds, as the API names them. */
function kindLabel(kind: string): string {
  const names: Record<string, [string, string, string, string]> = {
    Independent: ['Özel okul', 'Szkoła prywatna', 'Escola privada', 'Colegio privado'],
    'State academy': [
      'Devlet akademisi', 'Akademia publiczna', 'Academia pública', 'Academia pública',
    ],
    'State free school': [
      'Devlet free school', 'Publiczna free school', 'Free school pública',
      'Free school pública',
    ],
    'State school': ['Devlet okulu', 'Szkoła publiczna', 'Escola pública', 'Colegio público'],
    School: ['Okul', 'Szkoła', 'Escola', 'Colegio'],
  };
  const known = names[kind];
  return known ? tx(kind, ...known) : kind;
}

function genderLabel(gender: string): string {
  const key = gender.toLowerCase();
  if (key === 'boys') {
    return tx('boys', 'erkek',
    'chłopcy',
    'rapazes',
    'chicos');
  }
  if (key === 'girls') {
    return tx('girls', 'kız',
    'dziewczęta',
    'raparigas',
    'chicas');
  }
  return gender.toLowerCase();
}

function SchoolItem({ school }: { school: NearbySchool }) {
  const facts = [
    kindLabel(school.kind),
    school.ageRange ? tx(`ages ${school.ageRange}`, `${school.ageRange} yaş`,
    `wiek ${school.ageRange}`,
    `idades ${school.ageRange}`,
    `edades ${school.ageRange}`) : null,
    school.gender && school.gender !== 'Mixed' ? genderLabel(school.gender) : null,
    school.religiousCharacter,
    school.selective ? tx('selective', 'seçici',
    'z egzaminem wstępnym',
    'com seleção',
    'con selección') : null,
  ].filter((fact): fact is string => Boolean(fact));

  return (
    <li className="school">
      <div className="school__head">
        <span className="school__name">
          {school.website ? (
            <a href={school.website} target="_blank" rel="noreferrer">
              {school.name}
            </a>
          ) : (
            school.name
          )}
        </span>
        <span className="school__distance">
          {formatDistance(school.distanceMetres)} &middot;{' '}
          {tx(`~${school.walkMinutes} min walk`, `~${school.walkMinutes} dk yürüme`,
    `~${school.walkMinutes} min pieszo`,
    `~${school.walkMinutes} min a pé`,
    `~${school.walkMinutes} min andando`)}
        </span>
      </div>
      <div className="school__facts">{facts.join(' · ')}</div>
      <FillLine school={school} />
      <OfstedLine school={school} />
    </li>
  );
}

/**
 * Pupils on roll against the places the school is built for. Above 100% is labelled, because it
 * usually means every year group is full.
 */
function FillLine({ school }: { school: NearbySchool }) {
  if (school.fillPercent === null || school.fillPercent === undefined || !school.pupils || !school.capacity) {
    return null;
  }
  const fill = school.fillPercent;
  const tone = fill >= 100 ? 'bad' : fill >= 95 ? 'mixed' : 'good';
  const pupils = formatNumber(school.pupils);
  const capacity = formatNumber(school.capacity);
  return (
    <div className="school__fill">
      <span className="meter school__meter" aria-hidden="true">
        <span
          style={{
            width: `${Math.min(100, fill)}%`,
            background: tone === 'bad' ? 'var(--bad)' : tone === 'mixed' ? 'var(--mixed)' : 'var(--good)',
          }}
        />
      </span>
      <span className="tiny">
        {tx(`${fill}% full`, `%${fill} dolu`,
    `${fill}% zapełnienia`,
    `${fill}% de lotação`,
    `${fill}% de ocupación`)}
        <span className="muted"> &middot; {tx(`${pupils} pupils, ${capacity} places`, `${pupils} öğrenci, ${capacity} kontenjan`,
    `${pupils} uczniów, ${capacity} miejsc`,
    `${pupils} alunos, ${capacity} vagas`,
    `${pupils} alumnos, ${capacity} plazas`)}</span>
      </span>
      {fill > 100 ? (
        <span className={`badge badge--bad school__fill-badge`}>{tx('Over capacity', 'Kapasite üstü',
    'Powyżej pojemności',
    'Acima da lotação',
    'Por encima de su capacidad')}</span>
      ) : null}
    </div>
  );
}

function ofstedHeadline(headline: string): string {
  const names: Record<string, [string, string, string, string]> = {
    Outstanding: ['Üstün', 'Wyróżniająca', 'Excelente', 'Excelente'],
    Good: ['İyi', 'Dobra', 'Boa', 'Bueno'],
    'Requires improvement': [
      'Gelişmeli', 'Wymaga poprawy', 'Precisa de melhorar', 'Necesita mejorar',
    ],
    Inadequate: ['Yetersiz', 'Niewystarczająca', 'Inadequada', 'Insuficiente'],
    'Report card': ['Karne', 'Karta oceny', 'Ficha de avaliação', 'Ficha de evaluación'],
    'Ungraded inspection': [
      'Notsuz denetim', 'Kontrola bez oceny', 'Inspeção sem nota', 'Inspección sin calificación',
    ],
    Graded: ['Notlandırılmış', 'Z oceną', 'Com nota', 'Con calificación'],
  };
  const known = names[headline];
  return known ? tx(headline, ...known) : headline;
}

/** Report card areas, as Ofsted names them. */
function gradeArea(area: string): string {
  const names: Record<string, [string, string, string, string]> = {
    Achievement: ['Başarı', 'Osiągnięcia', 'Resultados', 'Resultados'],
    'Curriculum and teaching': [
      'Müfredat ve öğretim', 'Program i nauczanie', 'Currículo e ensino',
      'Currículo y enseñanza',
    ],
    'Attendance and behaviour': [
      'Devam ve davranış', 'Frekwencja i zachowanie', 'Assiduidade e comportamento',
      'Asistencia y comportamiento',
    ],
    'Personal development and wellbeing': [
      'Kişisel gelişim ve iyi olma', 'Rozwój osobisty i dobrostan',
      'Desenvolvimento pessoal e bem-estar', 'Desarrollo personal y bienestar',
    ],
    Inclusion: ['Kapsayıcılık', 'Włączanie', 'Inclusão', 'Inclusión'],
    'Leadership and governance': [
      'Liderlik ve yönetim', 'Kierownictwo i zarządzanie', 'Liderança e governação',
      'Liderazgo y gobernanza',
    ],
  };
  const known = names[area];
  return known ? tx(area, ...known) : area;
}

/** Report card grades on Ofsted's five-point scale. */
function gradeValue(grade: string): string {
  const names: Record<string, [string, string, string, string]> = {
    Exceptional: ['Olağanüstü', 'Wyjątkowy', 'Excecional', 'Excepcional'],
    'Strong standard': ['Güçlü', 'Wysoki poziom', 'Nível elevado', 'Nivel alto'],
    'Expected standard': [
      'Beklenen düzeyde', 'Poziom oczekiwany', 'Nível esperado', 'Nivel esperado',
    ],
    'Needs attention': [
      'İlgi gerektiriyor', 'Wymaga uwagi', 'Precisa de atenção', 'Requiere atención',
    ],
    'Urgent improvement': [
      'Acil iyileştirme', 'Pilna poprawa', 'Melhoria urgente', 'Mejora urgente',
    ],
  };
  const known = names[grade];
  return known ? tx(grade, ...known) : grade;
}

function OfstedLine({ school }: { school: NearbySchool }) {
  const ofsted = school.ofsted;
  if (!ofsted) {
    return (
      <div className="school__ofsted muted">
        {school.independent
          ? tx(
              'Independent school: inspected outside the state school data used here.',
              'Özel okul: burada kullanılan devlet okulu verilerinin dışında denetlenir.',
    'Szkoła prywatna: kontrolowana poza danymi o szkołach publicznych używanymi tutaj.',
    'Escola privada: inspecionada fora dos dados de escolas públicas usados aqui.',
    'Colegio privado: inspeccionado fuera de los datos de centros públicos que usamos aquí.',
            )
          : tx('No Ofsted judgement published for this school yet.', 'Bu okul için henüz Ofsted değerlendirmesi yayımlanmadı.',
    'Dla tej szkoły nie opublikowano jeszcze oceny Ofsted.',
    'Ainda não foi publicada avaliação do Ofsted para esta escola.',
    'Todavía no se ha publicado una valoración de Ofsted para este centro.')}
      </div>
    );
  }

  const when = ofsted.date ? formatDate(ofsted.date) : null;
  const tone = ofstedTone(ofsted.headline);

  return (
    <div className="school__ofsted">
      <span className={`badge badge--${tone}`}>Ofsted: {ofstedHeadline(ofsted.headline)}</span>
      {when ? (
        <span className="tiny muted">
          {' '}
          {framework(ofsted.framework)}, {when}
        </span>
      ) : null}
      {ofsted.detail ? <div className="tiny muted">{outcomeText(ofsted.detail)}</div> : null}
      {ofsted.grades.length > 0 ? (
        <ul className="school__grades">
          {ofsted.grades.map((grade) => (
            <li key={grade.area}>
              <span className="muted">{gradeArea(grade.area)}:</span> {gradeValue(grade.grade)}
            </li>
          ))}
        </ul>
      ) : null}
      {ofsted.safeguardingMet === false ? (
        <div className="tiny school__warning">
          <ShieldAlert size={12} aria-hidden="true" />{' '}
          {tx('Safeguarding judged not effective', 'Çocuk koruma etkili bulunmadı',
    'Ochrona dzieci oceniona jako nieskuteczna',
    'Proteção de menores avaliada como não eficaz',
    'Protección de menores valorada como no eficaz')}
        </div>
      ) : null}
      {ofsted.reportUrl ? (
        <div>
          <a className="tiny" href={ofsted.reportUrl} target="_blank" rel="noreferrer">
            {tx('Latest Ofsted report', 'Son Ofsted raporu',
    'Najnowszy raport Ofsted',
    'Relatório mais recente do Ofsted',
    'Informe más reciente de Ofsted')} <ExternalLink size={11} aria-hidden="true" />
          </a>
        </div>
      ) : null}
    </div>
  );
}

/** Ungraded inspection outcomes, as Ofsted words them. */
function outcomeText(text: string): string {
  const outcomes: Array<[string, string, string, string, string]> = [
    [
      'School remains Outstanding', 'Okul Üstün düzeyini koruyor',
      'Szkoła utrzymuje ocenę wyróżniającą', 'A escola mantém-se Excelente',
      'El centro se mantiene Excelente',
    ],
    [
      'School remains Good', 'Okul İyi düzeyini koruyor', 'Szkoła utrzymuje ocenę dobrą',
      'A escola mantém-se Boa', 'El centro se mantiene Bueno',
    ],
    [
      'Standards maintained', 'Standartlar korundu', 'Poziom utrzymany',
      'Padrões mantidos', 'Se mantiene el nivel',
    ],
    [
      'Improved significantly', 'Önemli ölçüde gelişti', 'Znaczna poprawa',
      'Melhorou significativamente', 'Ha mejorado notablemente',
    ],
    [
      'Some aspects not as strong', 'Bazı yönler eskisi kadar güçlü değil',
      'Niektóre obszary słabsze niż wcześniej', 'Alguns aspetos menos fortes',
      'Algunos aspectos menos sólidos',
    ],
  ];
  const match = outcomes.find(([english]) => text.startsWith(english));
  if (!match) {
    return text;
  }
  const rest = text.slice(match[0].length);
  // A full inspection follows either outcome, so the reason is worth carrying across.
  const concerns = rest.includes('Concerns');
  const improving = rest.includes('Improving');
  const notes: [string, string, string, string] = concerns
    ? [
        ' (endişeler var, tam denetim bekleniyor)',
        ' (zastrzeżenia, spodziewana pełna kontrola)',
        ' (preocupações, inspeção completa prevista)',
        ' (con reservas, se espera inspección completa)',
      ]
    : improving
      ? [
          ' (gelişiyor, tam denetim bekleniyor)',
          ' (poprawa, spodziewana pełna kontrola)',
          ' (a melhorar, inspeção completa prevista)',
          ' (mejorando, se espera inspección completa)',
        ]
      : ['', '', '', ''];
  return tx(
    text,
    match[1] + notes[0],
    match[2] + notes[1],
    match[3] + notes[2],
    match[4] + notes[3],
  );
}

function framework(value: string): string {
  switch (value) {
    case 'REPORT_CARD':
      return tx('report card', 'karne',
    'karta oceny',
    'ficha de avaliação',
    'ficha de evaluación');
    case 'UNGRADED':
      return tx('ungraded inspection', 'notsuz denetim',
    'kontrola bez oceny',
    'inspeção sem nota',
    'inspección sin calificación');
    default:
      return tx('graded inspection', 'notlu denetim',
    'kontrola z oceną',
    'inspeção com nota',
    'inspección con calificación');
  }
}

function ofstedTone(headline: string): 'good' | 'mixed' | 'bad' | 'neutral' {
  switch (headline) {
    case 'Outstanding':
    case 'Good':
      return 'good';
    case 'Requires improvement':
      return 'mixed';
    case 'Inadequate':
      return 'bad';
    default:
      return 'neutral';
  }
}

function formatDistance(metres: number): string {
  return metres < 1000
    ? `${Math.round(metres / 10) * 10} m`
    : `${(metres / 1000).toLocaleString(locale(), { maximumFractionDigits: 1, minimumFractionDigits: 1 })} km`;
}
